import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.getAttribute("data-loaded") === "1") resolve();
      else existing.addEventListener("load", () => resolve(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.setAttribute("data-loaded", "1");
      resolve();
    };
    script.onerror = () => reject(new Error("script"));
    document.head.appendChild(script);
  });
}

export default function SocialLogin({ onError }) {
  const { acceptSession } = useAuth();
  const [providers, setProviders] = useState(null);

  useEffect(() => {
    api.get("/auth/providers").then((response) => setProviders(response.data)).catch(() => setProviders({}));
  }, []);

  if (!providers || (!providers.google && !providers.apple)) return null;

  const finish = async (path, idToken, name) => {
    try {
      const response = await api.post(path, { id_token: idToken, name: name || "" });
      acceptSession(response.data);
    } catch (error) {
      onError?.(error.response?.data?.detail || "Sign-in failed.");
    }
  };

  const google = async () => {
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.isNativePlatform()) {
        const { SocialLogin } = await import("@capgo/capacitor-social-login");
        await SocialLogin.initialize({
          google: { webClientId: providers.google_client_id, iOSClientId: process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID, mode: "online" },
        });
        const result = await SocialLogin.login({ provider: "google", options: { scopes: ["email", "profile"] } });
        const idToken = result?.result?.idToken || result?.result?.id_token;
        if (!idToken) throw new Error("Google did not return an identity token.");
        await finish("/auth/google", idToken, result?.result?.profile?.name);
        return;
      }
    } catch (error) {
      if (error?.message && !String(error.message).includes("Cannot find")) {
        onError?.(error.message);
        return;
      }
    }
    try {
      await loadScript("https://accounts.google.com/gsi/client");
      window.google.accounts.id.initialize({
        client_id: providers.google_client_id,
        callback: (response) => finish("/auth/google", response.credential),
      });
      window.google.accounts.id.prompt();
    } catch {
      onError?.("Google sign-in is unavailable in this browser.");
    }
  };

  const apple = async () => {
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (Capacitor.getPlatform() === "ios") {
        const { AppleSignIn, SignInScope } = await import("@capawesome/capacitor-apple-sign-in");
        const result = await AppleSignIn.signIn({
          scopes: [SignInScope.Email, SignInScope.FullName],
        });
        const name = [result.givenName, result.familyName].filter(Boolean).join(" ");
        await finish("/auth/apple", result.idToken, name);
        return;
      }
    } catch (error) {
      if (error?.message && !String(error.message).includes("Cannot find")) {
        onError?.(error.message);
        return;
      }
    }
    try {
      await loadScript("https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js");
      window.AppleID.auth.init({
        clientId: providers.apple_client_id,
        scope: "name email",
        redirectURI: `${window.location.origin}/login`,
        usePopup: true,
      });
      const result = await window.AppleID.auth.signIn();
      const name = [result.user?.name?.firstName, result.user?.name?.lastName].filter(Boolean).join(" ");
      await finish("/auth/apple", result.authorization.id_token, name);
    } catch {
      onError?.("Sign in with Apple was cancelled or is not available.");
    }
  };

  const ios = typeof window !== "undefined" && window.Capacitor?.getPlatform?.() === "ios";
  const showGoogle = providers.google && (!ios || providers.apple);

  return (
    <div className="space-y-2">
      {showGoogle && (
        <button type="button" data-testid="login-google-button" onClick={google}
          className="w-full h-12 rounded-xl border border-zinc-200 bg-white font-semibold text-sm">
          Continue with Google
        </button>
      )}
      {providers.apple && (
        <button type="button" data-testid="login-apple-button" onClick={apple}
          className="w-full h-12 rounded-xl bg-zinc-900 text-white font-semibold text-sm">
          Sign in with Apple
        </button>
      )}
    </div>
  );
}
